import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import Spinner from '../../components/ui/Spinner';
import MessageBox from '../../components/ui/MessageBox';
import Button from '../../components/ui/Button';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit } from "lucide-react";

const SupplierListPage = ({ setView, setEditingContext }) => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'

  useEffect(() => {
    api.getSupplierListWithBalance(user.UsrName)
      .then(data => {
        const list = data.Table || [];
        setSuppliers(list);
        setFilteredSuppliers(list);
      })
      .catch(() => setError('Failed to fetch suppliers.'))
      .finally(() => setLoading(false));
  }, [user.UsrName]);

  useEffect(() => {
    const filtered = suppliers.filter(s => {
      const nameMatch = s.Vname?.toLowerCase().includes(searchTerm.toLowerCase());
      const mobileMatch = s.Mobile?.toLowerCase().includes(searchTerm.toLowerCase());
      const addressMatch =
        s.Address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.City?.toLowerCase().includes(searchTerm.toLowerCase());
      return nameMatch || mobileMatch || addressMatch;
    });
    setFilteredSuppliers(filtered);
  }, [searchTerm, suppliers]);

  return (
    <div className="flex flex-col h-full">
      {/* Header Section */}
      <div className="px-3 py-3 bg-grey border-b">
        {/* Row 1: Title */}
        <div className="mb-3">
          <Heading>Supplier Management</Heading>
        </div>

        {/* Row 2: New + Grid/Table */}
        <div className="flex items-center gap-2 mb-3">
          {/* New Button */}
          <Button
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
              rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
            onClick={() => {
              setEditingContext(null);
              setView('add-supplier');
            }}
          >
            <PlusCircle size={18} />
            <span className="font-medium">New</span>
          </Button>

          <div className="flex-1" />
          {/* Grid / Table Toggle */}
          <div className="flex items-center justify-center p-1 bg-white-100 rounded-lg">
            {/* Grid */}
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z" />
              </svg>
            </button>

            {/* Table */}
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Row 3: Back + Search Box */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("list")}
          >
            ⬅
          </Button>

          <SearchInput
            type="text"
            placeholder="Search by name, mobile, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>


      <div className="p-3">
        {loading ? (
          <Spinner />
        ) : error ? (
          <MessageBox message={error} />
        ) : filteredSuppliers.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No suppliers found.
          </div>
        ) : viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">Name</th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">Mobile</th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">City</th>
                  <th className="px-4 py-2 text-sm font-medium text-right text-gray-900 uppercase">Balance</th>
                  <th className="px-4 py-2 text-sm font-medium text-center text-gray-900 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSuppliers.map(supplier => (
                  <tr key={supplier.Vid}>
                    <td className="px-4 py-2">{supplier.Vname}</td>
                    <td className="px-4 py-2">{supplier.Mobile}</td>
                    <td className="px-4 py-2">{supplier.City}</td>
                    <td className="px-4 py-2 text-right">{supplier.Balance}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex justify-center items-center h-full">
                        <Button
                          onClick={() => {
                            setEditingContext(supplier.Vid);
                            setView('edit-supplier');
                          }}
                          className="flex items-center gap-2 px-3 py-1 text-xs 
                 font-medium text-white 
                 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
                 hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
                 transition-all duration-300 ease-in-out"
                        >
                          <FileEdit className="w-4 h-4" />
                          Edit
                        </Button>
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filteredSuppliers.map(supplier => (
              <div key={supplier.Vid} className="p-3 bg-white border rounded-lg shadow-sm">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">{supplier.Vname}</h3>
                  <p className="text-sm text-gray-600">{supplier.Mobile}</p>
                  <p className="text-xs text-gray-500">{supplier.City}</p>
                  <p className="text-xs font-medium text-gray-500">
                    Balance: <span className="text-gray-800">{supplier.Balance}</span>
                  </p>
                </div>
                <Button
                  className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium 
                 text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                  onClick={() => {
                    setEditingContext(supplier.Vid);
                    setView('edit-supplier');
                  }}
                >
                  <FileEdit className="w-5 h-5" />
                  Edit
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierListPage;
